import React, { useContext, useState } from 'react';
import { NormalPage } from '../../../components/layout/page/NormalPage';
import TextWinds from '../../../components/common/TextWinds';
import HFbutton from '../../../components/button/HFbutton';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { TermType } from '../../../components/common/Term';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import GuardianInput from '../../../components/student/first-login/GuardianInput';
import {
  TermAgreement,
  TermAgreementItem,
} from '../../../components/common/TermAgreement';
import { check14UnderAge, logout } from '../../../api/utils/util-func';
import FormRow from '../../../components/common/input/FormRow';
import ErrorInput from '../../../components/common/ErrorInput';
import {
  birthdayRules,
  DEFAULT_COUNTRY_CODE,
  ENGLISH_NAME_MESSAGE,
  englishNameRules,
  PASSWORD_MESSAGE,
  passwordRules,
  toCountryCodeRequest,
} from '../../../constants/validation';
import { StudentProfileContext } from '../StudentProfileContext';
import {
  putStudentFirstLogin,
  PutStudentFirstLoginRequest,
} from '../../../api/utils/student/first-login/apis';

/** 학생 최초 로그인 페이지 */
const StudentFirstLogin = () => {
  const dispatch = useAppDispatch();
  const { studentProfile } = useContext(StudentProfileContext);
  const uid = localStorage.getItem('userId');

  const methods = useForm<PutStudentFirstLoginRequest>({
    // 국가 코드는 직접 입력하되 기본값(한국)을 채워둔다.
    defaultValues: { guardianInfo: { countryCode: DEFAULT_COUNTRY_CODE } },
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const password = watch('password');
  const birthday = watch('birthday');

  /** 필수 약관 동의 여부 (두 약관 묶어서 한 개의 변수로 처리) */
  const [agreement, setAgreement] = useState<boolean>();
  /** 만 14세 미만의 어린이 여부. 미입력/미완성 생년월일은 false 로 처리된다. */
  const isMinor = check14UnderAge(birthday);

  const agreementList: TermAgreementItem[] = [
    { termType: TermType.SERVICE, essential: true },
    { termType: TermType.PRIVACY_CONSENT, essential: true },
  ];

  const onSubmit: SubmitHandler<PutStudentFirstLoginRequest> = (data) => {
    // 학생 필수 약관 동의 확인 (alert)
    if (!agreement) {
      dispatch(openAlert({ message: '약관에 동의해주세요' }));
      return;
    }

    // 보호자 필수 약관 동의 확인 (alert)
    if (isMinor && !data.guardianInfo?.agreementPersonalInfo) {
      dispatch(openAlert({ message: '보호자 약관에 동의해주세요.' }));
      return;
    }

    const params: PutStudentFirstLoginRequest = {
      ...data,
      englishName: data.englishName.toUpperCase(),
      agreementPersonalInfo: agreement,
      agreementServiceUsage: agreement,
    };

    if (isMinor) {
      // data 는 react-hook-form 의 폼 값 객체이므로 복사본에 담아 전달한다.
      params.guardianInfo = {
        ...data.guardianInfo,
        // 국가 코드는 폼에서 숫자만 다루므로 보낼 때 '+'를 붙인다.
        countryCode: toCountryCodeRequest(data.guardianInfo?.countryCode),
      };
    } else {
      delete params.guardianInfo;
    }

    putStudentFirstLogin(params)
      .then(() => {
        dispatch(
          openAlert({
            message: '입력이 완료 되었습니다. 다시 로그인 해주세요.',
            onClose: logout,
          }),
        );
      })
      .catch(() => {
        dispatch(
          openAlert({ message: '입력이 실패했습니다. 다시 시도해주세요' }),
        );
      });
  };

  return (
    <NormalPage bgColor="white" xlBgColor="gray0" hasHeader={false}>
      <div className="place-items-center pb-[65px] xl:mx-auto xl:w-[600px] xl:pb-10 xl:pt-8">
        <div className="bg-white xl:p-10">
          <FormProvider {...methods}>
            <form>
              <section>
                <TextWinds type="title_h1">내 정보 입력 (최초 1회)</TextWinds>
                <TextWinds type="content" className="mt-4">
                  이후 내 정보에서 수정 가능합니다.
                </TextWinds>

                <div className="mt-4 flex flex-col gap-2 rounded-lg bg-gray0 p-4">
                  <div className="flex items-center gap-2">
                    <TextWinds
                      type="content_body2"
                      className="w-14 shrink-0 font-semibold"
                    >
                      이름
                    </TextWinds>
                    {studentProfile ? (
                      <TextWinds type="content">
                        {studentProfile.name}
                      </TextWinds>
                    ) : (
                      <div className="h-6 w-24 animate-pulse rounded bg-gray3" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <TextWinds
                      type="content_body2"
                      className="w-14 shrink-0 font-semibold"
                    >
                      아이디
                    </TextWinds>
                    <TextWinds type="content">{uid ?? '-'}</TextWinds>
                  </div>
                </div>
              </section>

              <section className="mt-4">
                <TextWinds type="title_h3">내 정보</TextWinds>

                <div className="mt-4 flex flex-col gap-4 rounded-lg bg-gray0 p-4">
                  <div>
                    <TextWinds type={'content_body2'}>
                      새 비밀번호 설정
                    </TextWinds>
                    <FormRow
                      control={control}
                      name="password"
                      type="password"
                      placeholder={PASSWORD_MESSAGE}
                      rules={passwordRules({
                        required: '새 비밀번호를 입력해주세요',
                      })}
                      error={!!errors.password}
                    />
                    <div className="min-h-2">
                      {errors['password'] && (
                        <ErrorInput text={errors['password'].message} />
                      )}
                    </div>
                  </div>

                  <div>
                    <TextWinds type={'content_body2'}>
                      새 비밀번호 확인
                    </TextWinds>
                    <FormRow
                      control={control}
                      name="password2"
                      type="password"
                      placeholder="새 비밀번호를 한 번 더 입력해주세요"
                      rules={{
                        required: '새 비밀번호 확인을 입력해주세요',
                        validate: (value) => {
                          return (
                            value === password ||
                            '새 비밀번호와 일치하지 않습니다'
                          );
                        },
                      }}
                      error={!!errors.password2}
                    />
                    <div className="min-h-2">
                      {errors.password2 && (
                        <ErrorInput text={errors.password2.message} />
                      )}
                    </div>
                  </div>

                  <div>
                    <TextWinds type={'content_body2'}>영어이름</TextWinds>
                    <FormRow
                      control={control}
                      name="englishName"
                      type="text"
                      uppercase
                      placeholder={ENGLISH_NAME_MESSAGE}
                      rules={englishNameRules({
                        required: '영어이름을 입력해주세요',
                      })}
                      error={!!errors.englishName}
                    />
                    <div className="min-h-2">
                      {errors.englishName && (
                        <ErrorInput text={errors.englishName.message} />
                      )}
                    </div>
                  </div>

                  <div>
                    <TextWinds type={'content_body2'}>생년월일</TextWinds>

                    <FormRow
                      control={control}
                      name="birthday"
                      type="birthday"
                      rules={birthdayRules({ required: true })}
                      error={!!errors.birthday}
                    />
                    <div className="min-h-2">
                      {errors.birthday && (
                        <ErrorInput text={errors.birthday.message} />
                      )}
                    </div>
                  </div>

                  <div>
                    <TextWinds type={'content_body2'}>성별</TextWinds>
                    <FormRow
                      control={control}
                      name="gender"
                      type="gender"
                      placeholder="성별"
                      rules={{
                        required: '성별을 선택해주세요',
                      }}
                      error={!!errors.gender}
                    />
                    <div className="min-h-2">
                      {errors.gender && (
                        <ErrorInput text={errors.gender.message} />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 py-5">
                    <TermAgreement
                      agreementList={agreementList}
                      essentialChecked={setAgreement}
                    />
                  </div>
                </div>
              </section>

              {isMinor && (
                <>
                  <hr className="my-6 border-gray5" />
                  <GuardianInput
                    control={control}
                    errors={errors}
                    setAgree={(agree) =>
                      setValue('guardianInfo.agreementPersonalInfo', agree)
                    }
                  />
                </>
              )}

              <section className="mt-4 flex flex-col gap-2 xl:flex-row-reverse">
                <HFbutton
                  onClick={handleSubmit(onSubmit)}
                  type="primary-filled"
                >
                  저장
                </HFbutton>
                <HFbutton type="primary-outlined" onClick={() => logout()}>
                  취소
                </HFbutton>
              </section>
            </form>
          </FormProvider>
        </div>
      </div>
    </NormalPage>
  );
};

export default StudentFirstLogin;
